import { Center, CircularProgress, Collapse, HStack, Text, VStack } from '@chakra-ui/react'
import { CheckboxField, colors, FieldTypes, formatPhoneNumber, TextField } from '@hb/shared'
import { PhoneMultiFactorInfo } from 'firebase/auth'
import { useEffect, useRef } from 'react'
import { useCookies } from 'react-cookie'
import { usePhoneVerify } from '../../../hooks/backend/auth/usePhoneVerify'
import { SolidActionButton } from '../../Buttons'
import { CollapseError } from '../../CollapseError'
import { StandaloneInput } from '../../forms/Input'
import { InputRef } from '../../forms/Input/types'

const codeField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Verification Code',
}

const autoSendCodeField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'Automatically send code when I sign in',
}

export const PhoneVerify = ({ factor }: { factor: PhoneMultiFactorInfo }) => {
  const [{ automaticallySendSmsCode }, setCookie] = useCookies(['automaticallySendSmsCode'])
  const { handleVerify, sendCode, verificationId, verifying, sendingCode, error, code, setCode } =
    usePhoneVerify(factor, automaticallySendSmsCode)

  const verificationCodeInput = useRef<InputRef>(null)

  useEffect(() => {
    const onEnter = (e: KeyboardEvent) => {
      e.stopPropagation()
      if (e.key === 'Enter') {
        handleVerify()
      }
    }

    window.addEventListener('keydown', onEnter)
    return () => window.removeEventListener('keydown', onEnter)
  }, [handleVerify])

  useEffect(() => {
    if (verificationId && verificationCodeInput.current) {
      verificationCodeInput.current.focus()
    }
  }, [verificationId])
  return (
    <VStack px={1} w="100%" align="flex-start">
      <Collapse unmountOnExit in={!verificationId && !sendingCode} style={{ width: '100%' }}>
        <VStack spacing={1} align="flex-start" w="100%">
          <Text fontFamily="Hero-New" fontSize="md" color="gray.600">
            We'll send a verification code to {formatPhoneNumber(factor.phoneNumber)}.
          </Text>
          <SolidActionButton w="100%" size="sm" isLoading={sendingCode} onClick={sendCode}>
            Send Verification Code
          </SolidActionButton>
          <StandaloneInput
            field={autoSendCodeField}
            value={automaticallySendSmsCode}
            onChange={v => {
              setCookie('automaticallySendSmsCode', v)
            }}
          />
        </VStack>
      </Collapse>
      <Collapse unmountOnExit style={{ width: '100%' }} in={sendingCode}>
        <Center w="100%">
          <CircularProgress size={6} color={colors.green.hex} isIndeterminate />
          <Text fontFamily="Hero-New" fontSize="md" color="gray.600" ml={2}>
            Sending verification code...
          </Text>
        </Center>
      </Collapse>
      <Collapse unmountOnExit in={!!verificationId} style={{ width: '100%' }}>
        <VStack align="flex-start" w="100%" p={1}>
          <Text>Enter the code sent to your phone:</Text>
          <StandaloneInput
            ref={verificationCodeInput}
            theme="detailed"
            field={codeField}
            value={code}
            onChange={setCode}
          />
          <CollapseError error={error} />
          <HStack w="100%">
            <SolidActionButton isLoading={verifying} ml="auto" onClick={handleVerify}>
              Verify
            </SolidActionButton>
          </HStack>
        </VStack>
      </Collapse>
    </VStack>
  )
}
